import { Outlet } from 'react-router';
import type { Route } from './+types/layout';
import { apiFetch } from '~/lib/api-client';
import { SiteHeader } from '~/components/site-header';
import { SiteFooter } from '~/components/site-footer';
import { SubmitModalProvider } from '~/components/submit-modal';
import { AiOrb } from '~/components/ai-orb';
import { BottomTabBar } from '~/components/bottom-tab-bar';
import { NativeShell } from '~/components/native-shell';
import type { ApiResult } from '~/lib/types';
import { CopyProvider } from '~/lib/copy';

// Public shell. Loader exposes the request host (webcal:// links), the session
// (null when logged out), and whether the request is from the native app —
// detected server-side off the 'TBNCApp' UA marker so app chrome renders with
// no post-hydration flash. /api/me is a cheap in-process call; 401 → anon.
export async function loader({ request }: Route.LoaderArgs) {
  const host = new URL(request.url).host;
  const isApp = /TBNCApp/i.test(request.headers.get('user-agent') ?? '');
  let user: { email: string; role: string } | null = null;
  const res = await apiFetch(request, '/api/me');
  if (res.ok) {
    const body = (await res.json().catch(() => null)) as ApiResult<{
      userId: string;
      email: string;
      role: string;
    }> | null;
    if (body?.ok) user = { email: body.data.email, role: body.data.role };
  }

  // Which city's chrome this is. The header, the footer and the logo's
  // accessible name all named Tampa on every request — the shell around every
  // page announcing the wrong city.
  let siteName = 'Networking Calendar';
  let displayName = '';
  let areaLabel = '';
  const cfg = await apiFetch(request, '/api/config');
  if (cfg.ok) {
    const body = (await cfg.json().catch(() => null)) as ApiResult<{
      siteName: string;
      displayName: string;
      areas: { slug: string; label: string }[];
    }> | null;
    if (body?.ok) {
      siteName = body.data.siteName || siteName;
      displayName = body.data.displayName ?? '';
      // The hero's eyebrow is this city's own neighbourhoods, from its rows —
      // not a list typed into a component.
      areaLabel = (body.data.areas ?? [])
        .slice(0, 5)
        .map((a) => a.label)
        .join(' · ');
    }
  }

  return { host, user, isApp, siteName, displayName, areaLabel };
}

export default function PublicLayout({ loaderData }: Route.ComponentProps) {
  const { host, user, isApp, siteName, displayName, areaLabel } = loaderData;
  return (
    <CopyProvider
      vars={{ city: displayName, site: siteName, areas: areaLabel }}
      // Per-city overrides arrive with the blueprint engine's copy table. Until
      // then every city takes the declared default — the seam is what matters.
      overrides={{}}
    >
      <SubmitModalProvider>
        {isApp && <NativeShell />}
        <div className={isApp ? 'app-shell' : undefined}>
          <SiteHeader user={user} isApp={isApp} siteName={siteName} />
          <Outlet />
          {!isApp && <SiteFooter host={host} />}
          {/* AI concierge stays available in the app too (CSS lifts it above the tab bar). */}
          <AiOrb />
        </div>
        {isApp && <BottomTabBar user={user} />}
      </SubmitModalProvider>
    </CopyProvider>
  );
}
